import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Gavel } from "lucide-react";
import { myBidsQuery } from "@/lib/queries";
import { BidRow } from "@/components/bids/BidRow";
import { Button } from "@/components/ui/button";
import type { BidActivityRow } from "@/lib/auctions.functions";

export const Route = createFileRoute("/_authenticated/mina-bud")({
  head: () => ({
    meta: [
      { title: "Mina bud — Sellavo" },
      {
        name: "description",
        content: "Alla auktioner du budat på: pågående bud, vunna affärer och avslutade auktioner.",
      },
      { property: "og:title", content: "Mina bud — Sellavo" },
      {
        property: "og:description",
        content: "Se dina pågående bud, vad du vunnit och vilka auktioner som avslutats.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(myBidsQuery());
  },
  component: MyBidsPage,
});

type Tab = "live" | "won" | "done";

const TABS: { id: Tab; label: string }[] = [
  { id: "live", label: "Pågående" },
  { id: "won", label: "Vunna" },
  { id: "done", label: "Avslutade" },
];

function bucket(row: BidActivityRow): Tab {
  if (row.auctionStatus === "live") return "live";
  return row.status === "won" ? "won" : "done";
}

function MyBidsPage() {
  const { data, isLoading } = useQuery(myBidsQuery());
  const [tab, setTab] = useState<Tab>("live");
  const rows = data ?? [];
  const counts: Record<Tab, number> = {
    live: rows.filter((r) => bucket(r) === "live").length,
    won: rows.filter((r) => bucket(r) === "won").length,
    done: rows.filter((r) => bucket(r) === "done").length,
  };
  const shown = rows.filter((r) => bucket(r) === tab);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground sm:text-4xl">
        Mina bud
      </h1>
      <p className="mt-1 text-sm text-muted-foreground sm:mt-2 sm:text-base">
        Här ser du allt du budat på – vad du leder, vad du vunnit och vad som är avslutat.
      </p>

      <div className="mt-5 flex gap-2 rounded-xl bg-muted p-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition ${
              tab === t.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label} {counts[t.id] > 0 && <span className="opacity-70">({counts[t.id]})</span>}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" aria-hidden />
            ))}
          </div>
        ) : shown.length ? (
          <ul className="space-y-3">
            {shown.map((row) => (
              <BidRow key={row.slug} row={row} />
            ))}
          </ul>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
            <Gavel className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
            <p className="mt-3 text-sm text-muted-foreground">
              {tab === "live"
                ? "Du har inga pågående bud just nu."
                : tab === "won"
                  ? "Du har inte vunnit någon auktion än."
                  : "Inga avslutade auktioner att visa."}
            </p>
            <Button asChild className="mt-4">
              <Link to="/annonser" search={{ salj: "auction" } as never}>
                Se pågående auktioner
              </Link>
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
